<template>
  <div>
    <div v-for="section in populatedSections" :key="section.title" class="mb-4">
      <div class="text-subtitle-2 text-primary weight-600 mb-2">{{ section.title }}</div>

      <div v-for="field in section.fields" :key="field.key" class="autofill-field">
        <div class="autofill-field__label">
          <span class="text-body-2 weight-600">{{ field.label }}</span>
          <div class="text-caption text-medium-emphasis">
            <template v-if="currentValue(field.key)">
              Currently: <span class="weight-600">{{ currentValue(field.key) }}</span>
            </template>
            <template v-else>Currently empty</template>
          </div>
        </div>

        <div class="autofill-field__candidates">
          <v-chip
            v-for="candidate in props.candidates[field.key]"
            :key="candidate.value"
            :color="isApplied(field.key, candidate) ? 'success' : 'primary'"
            :variant="isApplied(field.key, candidate) ? 'flat' : 'outlined'"
            :prepend-icon="isApplied(field.key, candidate) ? 'mdi-check' : confidenceIcon(candidate.confidence)"
            class="mr-2 mb-2"
            @click="emit('apply', { field: field.key, value: candidate.value })">
            {{ candidate.value }}
            <v-tooltip activator="parent" location="top" max-width="400">
              <div>
                <strong>{{ candidate.sourceDocument || "Uploaded document" }}</strong>
                <span v-if="candidate.sourcePage"> — page {{ candidate.sourcePage }}</span>
              </div>
              <div v-if="candidate.evidence">“{{ candidate.evidence }}”</div>
              <div>Confidence: {{ candidate.confidence }}</div>
              <div v-if="isApplied(field.key, candidate)">Already applied to the form.</div>
            </v-tooltip>
          </v-chip>
        </div>
      </div>
    </div>

    <!-- Diagnoses are pairs, and an order takes one primary plus any number of secondaries,
         so each candidate offers both slots instead of a single apply. -->
    <div v-if="props.diagnoses.length > 0" class="mb-4">
      <div class="text-subtitle-2 text-primary weight-600 mb-2">Diagnoses</div>
      <div class="text-caption text-medium-emphasis mb-2">
        The first diagnosis is set as the primary; pick a different one, and add any others as secondary.
      </div>

      <div v-for="(diagnosis, index) in props.diagnoses" :key="diagnosisKey(diagnosis)" class="autofill-field">
        <div class="autofill-field__label">
          <span class="text-body-2 weight-600">{{ diagnosis.icd10Code || "No code" }}</span>
          <v-chip v-if="diagnosis.isPrimary" size="x-small" color="primary" variant="tonal" class="ml-2">
            marked primary
          </v-chip>
          <div class="text-caption text-medium-emphasis">{{ diagnosis.name }}</div>
          <div class="text-caption text-medium-emphasis">
            <v-icon :icon="confidenceIcon(diagnosis.confidence)" size="14"></v-icon>
            {{ diagnosis.sourceDocument || "Uploaded document"
            }}<span v-if="diagnosis.sourcePage"> — page {{ diagnosis.sourcePage }}</span>
          </div>
          <div v-if="diagnosis.evidence" class="text-caption text-medium-emphasis">“{{ diagnosis.evidence }}”</div>
        </div>

        <div class="autofill-field__candidates">
          <v-btn
            :color="isAppliedPrimary(diagnosis) ? 'success' : 'primary'"
            :variant="isAppliedPrimary(diagnosis) ? 'flat' : 'outlined'"
            :prepend-icon="isAppliedPrimary(diagnosis) ? 'mdi-check' : 'mdi-numeric-1-circle-outline'"
            size="small"
            class="mr-2 mb-2"
            @click="emit('applyDiagnosis', { slot: SLOT.PRIMARY, diagnosis })">
            {{ isAppliedPrimary(diagnosis) ? "Primary diagnosis" : "Set as primary" }}
          </v-btn>
          <v-btn
            :color="isAppliedSecondary(diagnosis) ? 'success' : 'primary'"
            :variant="isAppliedSecondary(diagnosis) ? 'flat' : 'outlined'"
            :prepend-icon="isAppliedSecondary(diagnosis) ? 'mdi-check' : 'mdi-plus'"
            :disabled="isAppliedPrimary(diagnosis) || isAppliedSecondary(diagnosis)"
            size="small"
            class="mb-2"
            @click="emit('applyDiagnosis', { slot: SLOT.SECONDARY, diagnosis, index })">
            {{ isAppliedSecondary(diagnosis) ? "Added as secondary" : "Add as secondary" }}
          </v-btn>
        </div>
      </div>
    </div>

    <!-- The ticket requires unfound fields to read as empty rather than be guessed at. -->
    <v-expansion-panels v-if="missingFields.length > 0" variant="accordion" class="mt-2">
      <v-expansion-panel>
        <v-expansion-panel-title class="text-body-2">
          <v-icon icon="mdi-alert-circle-outline" size="18" class="mr-2"></v-icon>
          {{ missingFields.length }} {{ missingFields.length === 1 ? "field was" : "fields were" }} not found in the
          documents
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <div class="text-body-2 text-medium-emphasis mb-2">
            Nothing was read for these — fill them in by hand, or upload a document that shows them and run autofill
            again.
          </div>
          <v-chip v-for="field in missingFields" :key="field.key" size="small" variant="outlined" class="mr-2 mb-2">
            {{ field.label }}
          </v-chip>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { AUTOFILL_CONFIDENCE, AUTOFILL_CONFIDENCE_ICONS, AUTOFILL_DIAGNOSIS_SLOT } from "@/constants/autofill";
import { convertStateToFullName } from "@/utils/usaStates";

// Field keys match orderInfo's own keys, so an applied value goes straight to
// updateOrderInfoField. The backend sends snake_case; the axios interceptor camelizes it.
// Only list fields the create-submission form actually renders: applying a chip writes the value
// to the order, so a field with no control here would commit a value the user can't see or fix.
// Mirrors AutofillField in portal/logic/ai/create_submission_autofill.py.
const SECTIONS = [
  {
    title: "Patient",
    fields: [
      { key: "patientFirstName", label: "First name" },
      { key: "patientLastName", label: "Last name" },
      { key: "patientDob", label: "Date of birth" },
      { key: "patientGender", label: "Gender" },
      { key: "patientPhone", label: "Phone" },
      { key: "patientAddress1", label: "Address line 1" },
      { key: "patientCity", label: "City" },
      { key: "patientState", label: "State" },
      { key: "patientZip", label: "ZIP" },
    ],
  },
  {
    title: "Insurance",
    fields: [
      { key: "insuranceProvider", label: "Insurance provider" },
      { key: "patientMemberId", label: "Member ID" },
      { key: "benefitType", label: "Benefit category" },
      { key: "bin", label: "Rx BIN" },
      { key: "processorControlNumber", label: "Rx PCN" },
      { key: "grpNumber", label: "Rx group number" },
    ],
  },
  {
    title: "Provider",
    fields: [
      { key: "providerFirstName", label: "First name" },
      { key: "providerLastName", label: "Last name" },
      { key: "providerNpiNumber", label: "NPI" },
      { key: "providerContactPhone", label: "Contact phone" },
      { key: "providerContactFax", label: "Contact fax" },
      { key: "providerOfficeName", label: "Office name" },
      { key: "providerAddress1", label: "Address line 1" },
      { key: "providerCity", label: "City" },
      { key: "providerState", label: "State" },
      { key: "providerZip", label: "ZIP" },
    ],
  },
  {
    title: "Prescription",
    fields: [
      { key: "medication", label: "Medication" },
      { key: "nationalDrugCode", label: "NDC" },
      { key: "dailyDosage", label: "Daily dosage" },
      { key: "frequency", label: "Frequency" },
      { key: "packageSizeUnit", label: "Package size unit" },
      { key: "quantityPer30Days", label: "Quantity per 30 days" },
      { key: "expectedLengthOfTherapy", label: "Duration of therapy" },
    ],
  },
];

// Diagnoses are reported separately from SECTIONS (they are pairs, and there can be several),
// so they get their own entry in the not-found list.
const DIAGNOSIS_MISSING_LABEL = "Diagnosis: primary diagnosis and ICD-10";

const props = defineProps({
  // { orderInfoFieldKey: [{ value, sourceDocument, sourcePage, confidence, evidence }] }
  candidates: {
    type: Object,
    required: true,
  },
  // [{ name, icd10Code, isPrimary, sourceDocument, sourcePage, confidence, evidence }]
  diagnoses: {
    type: Array,
    default() {
      return [];
    },
  },
  orderInfo: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(["apply", "applyDiagnosis"]);

const SLOT = AUTOFILL_DIAGNOSIS_SLOT;

const hasCandidates = (key) => Array.isArray(props.candidates[key]) && props.candidates[key].length > 0;

const populatedSections = computed(() =>
  SECTIONS.map((section) => ({ ...section, fields: section.fields.filter((f) => hasCandidates(f.key)) })).filter(
    (section) => section.fields.length > 0
  )
);

const missingFields = computed(() => {
  const missing = SECTIONS.flatMap((section) =>
    section.fields
      .filter((f) => !hasCandidates(f.key))
      .map((f) => ({ key: f.key, label: `${section.title}: ${f.label}` }))
  );
  if (props.diagnoses.length === 0) {
    missing.push({ key: "diagnoses", label: DIAGNOSIS_MISSING_LABEL });
  }
  return missing;
});

const diagnosisKey = (diagnosis) => `${diagnosis.icd10Code || ""}|${diagnosis.name}`;

const sameDiagnosis = (a, b) =>
  (a?.name || "").trim().toLowerCase() === (b?.name || "").trim().toLowerCase() &&
  (a?.icd10Code || "").trim().toLowerCase() === (b?.icd10Code || "").trim().toLowerCase();

function isAppliedPrimary(diagnosis) {
  return sameDiagnosis(
    {
      name: props.orderInfo?.primaryDiagnosisName,
      icd10Code: props.orderInfo?.primaryDiagnosisIcd10Code,
    },
    diagnosis
  );
}

function isAppliedSecondary(diagnosis) {
  return (props.orderInfo?.secondaryDiagnosis || []).some((entry) => entry?.name && sameDiagnosis(entry, diagnosis));
}

function currentValue(key) {
  const value = props.orderInfo?.[key];
  return value === null || value === undefined || value === "" ? "" : String(value);
}

function isApplied(key, candidate) {
  // State chips are applied as full names ("CA" fills in "California"), so compare the converted value.
  if (key === "patientState" || key === "providerState") {
    return currentValue(key) !== "" && currentValue(key) === convertStateToFullName(candidate.value);
  }
  return currentValue(key) === String(candidate.value);
}

function confidenceIcon(confidence) {
  return AUTOFILL_CONFIDENCE_ICONS[confidence] || AUTOFILL_CONFIDENCE_ICONS[AUTOFILL_CONFIDENCE.MEDIUM];
}
</script>

<style lang="scss" scoped>
@import "../styles/main.scss";

.autofill-field {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid rgba(94, 120, 152, 0.15);

  &__label {
    flex: 0 0 220px;
    min-width: 160px;
  }

  &__candidates {
    flex: 1 1 320px;
    min-width: 0;
  }
}
</style>
